/* eslint-disable @typescript-eslint/member-delimiter-style */
import React, { useState, useEffect, useCallback } from 'react'
import { Grid, Card, CardContent, Typography, Button, Box } from '@material-ui/core'
import { TimePicker } from '@material-ui/pickers'
import SaveIcon from '@material-ui/icons/Save'

import moment, { Moment } from 'config/moment'
import { snackbar } from 'components'
import { useApiCall } from 'hooks'

import { Daypart, GetDaypartsResponse, UpdateDaypartsPayload } from './types'
import { getDayparts, updateDayparts } from './api'
import { useStyles } from './styles'

const DAYPART_CONFIG = [
  { code: 'BREAKFAST', title: 'Mañana', icon: '☕' },
  { code: 'LUNCH', title: 'Mediodía', icon: '🍽️' },
  { code: 'SNACK', title: 'Tarde', icon: '🍰' },
  { code: 'DINNER', title: 'Noche', icon: '🌙' },
]

interface TimeSlot {
  from: Moment | null
  to: Moment | null
}

const Dayparts: React.FC = () => {
  const classes = useStyles()
  const [timeSlots, setTimeSlots] = useState<Record<string, TimeSlot>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getDaypartsApi = useCallback((_data?: void) => getDayparts(), [])
  const [getDaypartsCall] = useApiCall<void, GetDaypartsResponse>(getDaypartsApi)
  const [updateDaypartsCall] = useApiCall<UpdateDaypartsPayload, { dayparts: Daypart[] }>(
    updateDayparts,
  )

  const getDefaultTimeSlot = useCallback((code: string): TimeSlot => {
    const defaults: Record<string, { from: string; to: string }> = {
      BREAKFAST: { from: '07:00', to: '11:59' },
      LUNCH: { from: '12:00', to: '15:59' },
      SNACK: { from: '16:00', to: '19:59' },
      DINNER: { from: '20:00', to: '23:59' },
    }
    const defaultTime = defaults[code] || { from: '00:00', to: '23:59' }
    return {
      from: moment(defaultTime.from, 'HH:mm'),
      to: moment(defaultTime.to, 'HH:mm'),
    }
  }, [])

  const loadDayparts = useCallback(async () => {
    try {
      const response = await getDaypartsCall(undefined)
      const initialTimeSlots: Record<string, TimeSlot> = {}

      if (response?.dayparts && response.dayparts.length > 0) {
        response.dayparts.forEach((daypart: Daypart) => {
          initialTimeSlots[daypart.code] = {
            from: daypart.timeFrom ? moment(daypart.timeFrom, ['HH:mm:ss', 'HH:mm']) : null,
            to: daypart.timeTo ? moment(daypart.timeTo, ['HH:mm:ss', 'HH:mm']) : null,
          }
        })
      }

      // Set defaults for any missing dayparts
      DAYPART_CONFIG.forEach(config => {
        if (!initialTimeSlots[config.code]) {
          initialTimeSlots[config.code] = getDefaultTimeSlot(config.code)
        }
      })

      setTimeSlots(initialTimeSlots)
    } catch (err) {
      console.error('Error loading dayparts:', err)
      snackbar.show('Error al cargar los dayparts')
      // Set defaults on error
      const initialTimeSlots: Record<string, TimeSlot> = {}
      DAYPART_CONFIG.forEach(config => {
        initialTimeSlots[config.code] = getDefaultTimeSlot(config.code)
      })
      setTimeSlots(initialTimeSlots)
    }
  }, [getDaypartsCall, getDefaultTimeSlot])

  useEffect(() => {
    loadDayparts()
  }, [loadDayparts])

  const handleTimeChange = (code: string, field: 'from' | 'to', value: Moment | null) => {
    if (value) {
      // Ensure we preserve the 24-hour format
      const hours = value.hours()
      const minutes = value.minutes()
      const correctedValue = moment()
        .hours(hours)
        .minutes(minutes)
        .seconds(0)

      setTimeSlots(prev => ({
        ...prev,
        [code]: {
          ...prev[code],
          [field]: correctedValue,
        },
      }))
    } else {
      setTimeSlots(prev => ({
        ...prev,
        [code]: {
          ...prev[code],
          [field]: null,
        },
      }))
    }
    // Clear error for this daypart when user changes time
    setErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors[code]
      return newErrors
    })
  }

  const expandToIntervals = (fromMinutes: number, toMinutes: number) => {
    if (fromMinutes < toMinutes) {
      return [{ from: fromMinutes, to: toMinutes }]
    }
    if (fromMinutes > toMinutes) {
      // Overnight (DINNER): split across midnight
      return [
        { from: fromMinutes, to: 1439 },
        { from: 0, to: toMinutes },
      ]
    }
    return []
  }

  const rangesOverlap = (from1: number, to1: number, from2: number, to2: number): boolean => {
    const intervals1 = expandToIntervals(from1, to1)
    const intervals2 = expandToIntervals(from2, to2)
    return intervals1.some(i1 => intervals2.some(i2 => i1.to >= i2.from && i2.to >= i1.from))
  }

  const validateTimeSlots = (): boolean => {
    const newErrors: Record<string, string> = {}
    const slots: Array<{ code: string; from: Moment; to: Moment }> = []

    // Check all fields are filled
    DAYPART_CONFIG.forEach(config => {
      const slot = timeSlots[config.code]
      if (!slot?.from || !slot?.to) {
        newErrors[config.code] = 'Debe completar ambos campos de hora'
        setErrors(newErrors)
        return
      }

      const fromMinutes = slot.from.hours() * 60 + slot.from.minutes()
      const toMinutes = slot.to.hours() * 60 + slot.to.minutes()

      // Equal times always invalid. Overnight (from > to) only for DINNER.
      if (fromMinutes === toMinutes) {
        newErrors[config.code] = 'La hora de inicio y fin no pueden ser iguales'
        setErrors(newErrors)
        return
      }
      if (fromMinutes > toMinutes && config.code !== 'DINNER') {
        newErrors[config.code] = 'La hora de inicio debe ser menor que la hora de fin'
        setErrors(newErrors)
        return
      }

      slots.push({
        code: config.code,
        from: slot.from,
        to: slot.to,
      })
    })

    if (Object.keys(newErrors).length > 0) {
      return false
    }

    for (let i = 0; i < slots.length; i += 1) {
      for (let j = i + 1; j < slots.length; j += 1) {
        const slot1 = slots[i]
        const slot2 = slots[j]

        const slot1From = slot1.from.hours() * 60 + slot1.from.minutes()
        const slot1To = slot1.to.hours() * 60 + slot1.to.minutes()
        const slot2From = slot2.from.hours() * 60 + slot2.from.minutes()
        const slot2To = slot2.to.hours() * 60 + slot2.to.minutes()

        if (rangesOverlap(slot1From, slot1To, slot2From, slot2To)) {
          const slot2Title = DAYPART_CONFIG.find(c => c.code === slot2.code)?.title || slot2.code
          const slot1Title = DAYPART_CONFIG.find(c => c.code === slot1.code)?.title || slot1.code
          newErrors[slot1.code] = `El rango horario se solapa con ${slot2Title}`
          newErrors[slot2.code] = `El rango horario se solapa con ${slot1Title}`
        }
      }
    }

    // Check all slots are within 00:00 - 23:59
    slots.forEach(slot => {
      const fromMinutes = slot.from.hours() * 60 + slot.from.minutes()
      const toMinutes = slot.to.hours() * 60 + slot.to.minutes()

      if (fromMinutes < 0 || fromMinutes >= 1440 || toMinutes < 0 || toMinutes > 1439) {
        newErrors[slot.code] = 'Los horarios deben estar entre las 00:00 y las 23:59'
      }
    })

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return false
    }

    setErrors({})
    return true
  }

  const handleSave = async () => {
    if (!validateTimeSlots()) {
      snackbar.show('Por favor corrija los errores antes de guardar')
      return
    }

    setIsLoading(true)
    try {
      const payload: UpdateDaypartsPayload = {
        dayparts: DAYPART_CONFIG.map(config => {
          const slot = timeSlots[config.code]
          if (!slot?.from || !slot?.to) {
            const defaultSlot = getDefaultTimeSlot(config.code)
            return {
              code: config.code,
              title: config.title,
              timeFrom: defaultSlot.from!.format('HH:mm:ss'),
              timeTo: defaultSlot.to!.format('HH:mm:ss'),
            }
          }
          return {
            code: config.code,
            title: config.title,
            timeFrom: slot.from.format('HH:mm:ss'),
            timeTo: slot.to.format('HH:mm:ss'),
          }
        }),
      }

      const response = await updateDaypartsCall(payload)
      if (response?.dayparts) {
        snackbar.show('Dayparts guardados correctamente')
        await loadDayparts()
      }
    } catch (err) {
      console.error('Error saving dayparts:', err)
      const errorMessage = err instanceof Error ? err.message : 'Error al guardar los dayparts'
      snackbar.show(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={classes.container}>
      <Typography variant="h4" className={classes.title}>
        Dayparts
      </Typography>
      <Typography variant="body1" className={classes.description}>
        Configura los rangos horarios para cada momento del día
      </Typography>

      <Grid container spacing={3}>
        {DAYPART_CONFIG.map(config => {
          const slot = timeSlots[config.code]
          const error = errors[config.code]

          return (
            <Grid item xs={12} sm={6} key={config.code}>
              <Card className={classes.card}>
                <CardContent className={classes.cardContent}>
                  <Typography variant="h6" className={classes.cardTitle}>
                    <span className={classes.icon}>{config.icon}</span>
                    {config.title}
                  </Typography>

                  <div className={classes.timeContainer}>
                    <div className={classes.timeField}>
                      <TimePicker
                        label="Desde"
                        value={slot?.from || null}
                        onChange={value => handleTimeChange(config.code, 'from', value)}
                        format="HH:mm"
                        ampm
                        views={['hours', 'minutes']}
                        autoOk
                        disabled={isLoading}
                        error={!!error}
                        helperText={error || ''}
                        fullWidth
                        minutesStep={1}
                      />
                    </div>
                    <div className={classes.timeField}>
                      <TimePicker
                        label="Hasta"
                        value={slot?.to || null}
                        onChange={value => handleTimeChange(config.code, 'to', value)}
                        format="HH:mm"
                        ampm
                        views={['hours', 'minutes']}
                        autoOk
                        disabled={isLoading}
                        error={!!error}
                        fullWidth
                        minutesStep={1}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Grid>
          )
        })}
      </Grid>

      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={isLoading}
          className={classes.saveButton}
        >
          Guardar Cambios
        </Button>
      </Box>
    </div>
  )
}

export { Dayparts }
